/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { type RequestActionType, type RequestType } from '@strata/react-chat';
import { type GridWithFilters } from '@strata/react-grid';
import { type PayerRequestStatusIndicator } from '@strata/react-status-indicators';
import { type ComponentProps } from 'react';

import { PAYER_REQUEST_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { type TimeFrameOption } from 'services/interfaces';
import { getBaseQuery, jsonToFormData } from 'store/api/apiBaseQuery';

export interface RequestPayload {
    payerId: string;
    segmentId: string;
    requestType: (typeof RequestType)[keyof typeof RequestType];
    requestSubType?: string;
    typeLevel2?: string;
    memberId?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    urgent: boolean;
    pbmOnly: boolean;
    additionalNotes?: string;
    treatmentIds: string[];
    files: File[];
    assignedUpn?: string;
    payerAssignedUpn?: string;
}

export interface OpenRequestSummary {
    requestId: string;
    requestSubType?: string;
    typeLevel2?: string;
    summary?: string;
    additionalNotes?: string;
    memberId?: string;
    createDate: string;
    submitterUpn: string;
    submitterFullName?: string;
    payerAssignedUpn?: string;
    payerAssignedFullName?: string;
    issueSource?: string;
    isUnread: boolean;
    requestNumber: string;
    urgent: boolean;
    requestStatus: (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses];
    lastUpdated: string;
    resolvedDate?: string;
    pbmOnly?: boolean;
}

export interface ArchivedRequestSummary {
    request_id: string;
    request_type: (typeof RequestType)[keyof typeof RequestType];
    request_sub_type?: string;
    type_level_2?: string;
    request_number: string;
    summary?: string;
    member_id?: string;
    submitter_upn: string;
    submitter_full_name?: string;
    is_unread: boolean;
    urgent: boolean;
    pbm_only?: boolean;
    request_status: (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses];
    completed_date?: string;
    resolved_at?: string;
    issue_source?: string;
    payer_assigned_upn?: string;
}

export interface RequestDetails {
    requestId: string;
    segmentId: string;
    requestType: Exclude<
        (typeof RequestType)[keyof typeof RequestType],
        typeof RequestType.ARCHIVE
    >;
    requestSubType?: string;
    typeLevel2?: string;
    summary?: string;
    memberId?: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    benefitGroup?: string;
    createDate: string;
    submitterUpn: string;
    submitterFullName?: string;
    requestNumber: string;
    reopenedFromRequestId?: string;
    reopenedFromRequestNumber?: string;
    issueSource?: string;
    primaryIssueDescription?: string;
    secondaryIssueDescription?: string;
    requestStatus: (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses];
    assignedUpn?: string;
    payerAssignedUpn?: string;
    urgent: boolean;
    additionalNotes?: string;
    treatmentIds: string[];
    attachments: {
        requestAttachmentId: string;
        filename: string;
        dateAdded: string;
    }[];
    activity: {
        requestActivityId: string;
        actionDate: string;
        actionType: (typeof RequestActionType)[keyof typeof RequestActionType];
        actorUpn?: string;
        fmhAction: boolean;
        pbmAction?: boolean;
        actionDetail?: string;
    }[];
    pbmOnly?: boolean;
}

export type RequestDetailsSummary = Pick<
    RequestDetails,
    | 'requestId'
    | 'requestNumber'
    | 'requestSubType'
    | 'typeLevel2'
    | 'requestStatus'
    | 'createDate'
    | 'primaryIssueDescription'
    | 'secondaryIssueDescription'
    | 'issueSource'
    | 'pbmOnly'
> & { completedDate?: string };

/** Props for all payer request action dialogs */
export interface PayerRequestActionDialogProps {
    requestDetails: RequestDetails;
    payerId: string;
    onClose: () => void;
}

const TagType = {
    REQUESTS_COUNTS: 'REQUESTS_COUNTS',
    REQUEST_LIST: 'REQUEST_LIST',
    REQUESTS: 'REQUESTS',
    TREATMENT_REQUESTS: 'TREATMENT_REQUESTS',
} as const;

export const payerRequestApi = createApi({
    baseQuery: getBaseQuery(PAYER_REQUEST_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'payerRequestApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getOpenRequestCounts: build.query<
            Record<(typeof RequestType)[keyof typeof RequestType], number>,
            { payerId: string }
        >({
            query: ({ payerId }) => `${payerId}/requests/counts`,
            transformResponse: (response: {
                open_requests: Record<(typeof RequestType)[keyof typeof RequestType], number>;
            }) => response?.open_requests,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving request counts.',
            ),
            providesTags: [{ type: TagType.REQUESTS_COUNTS }],
        }),
        getOpenRequestsByType: build.query<
            { filteredCount: number; requests: OpenRequestSummary[] },
            {
                payerId: string;
                requestType: (typeof RequestType)[keyof typeof RequestType];
                segmentIds: string[];
            }
        >({
            query: ({ payerId, requestType, segmentIds }) => ({
                url: `${payerId}/requests`,
                params: { request_type: requestType, segment_ids: segmentIds },
            }),
            transformResponse: (response: {
                filtered_count: number;
                requests: {
                    request_id: string;
                    request_sub_type?: string;
                    type_level_2?: string;
                    summary?: string;
                    member_id?: string;
                    create_date: string;
                    submitter_upn: string;
                    is_unread: boolean;
                    request_number: number;
                    urgent: boolean;
                    pbm_only?: boolean;
                    payer_assigned_upn?: string;
                    issue_source?: string;
                    request_status: (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses];
                    additional_notes?: string;
                    last_updated: string;
                    resolved_at?: string;
                }[];
            }) => ({
                filteredCount: response?.filtered_count,
                requests: response?.requests?.map((request) => ({
                    requestId: request.request_id,
                    requestSubType: request.request_sub_type,
                    typeLevel2: request.type_level_2,
                    summary: request.summary,
                    additionalNotes: request.additional_notes,
                    memberId: request.member_id,
                    createDate: request.create_date,
                    submitterUpn: request.submitter_upn,
                    payerAssignedUpn: request.payer_assigned_upn,
                    issueSource: request.issue_source,
                    isUnread: request.is_unread,
                    requestNumber: request.request_number?.toString().padStart(6, '0'),
                    urgent: request.urgent,
                    pbmOnly: request.pbm_only,
                    requestStatus: request.request_status,
                    lastUpdated: request.last_updated,
                    resolvedDate: request.resolved_at,
                })),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving requests.'),
            providesTags: [TagType.REQUEST_LIST],
        }),
        getArchivedRequests: build.query<
            {
                count: number;
                filteredCount: number;
                filters: { key: string; values: string[] }[];
                requests: ArchivedRequestSummary[];
            },
            {
                payerId: string;
                page: number;
                segmentIds: string[];
                searchText?: string;
                timeframe?: TimeFrameOption;
                requestType?: (typeof RequestType)[keyof typeof RequestType][];
                requestSubType?: string[];
                typeLevel2?: string[];
                issueSource?: string[];
                requestStatus?: (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses][];
                urgent?: boolean;
                pbmOnly?: boolean;
                sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
            }
        >({
            query: ({
                page,
                payerId,
                segmentIds,
                searchText,
                timeframe,
                requestType,
                requestSubType,
                typeLevel2,
                issueSource,
                requestStatus,
                urgent,
                pbmOnly,
                sort,
            }) => ({
                url: `${payerId}/requests/archive`,
                params: {
                    page,
                    segment_ids: segmentIds,
                    search_text: searchText,
                    timeframe,
                    request_type: requestType,
                    request_sub_type: requestSubType,
                    type_level_2: typeLevel2,
                    issue_source: issueSource,
                    request_status: requestStatus,
                    urgent,
                    pbm_only: pbmOnly,
                    sort_col: sort?.[0].id,
                    sort_desc: sort?.[0].desc,
                },
            }),
            transformResponse: (response: {
                count: number;
                filtered_count: number;
                filters: { key: string; values: string[] }[];
                requests: (Omit<
                    ArchivedRequestSummary,
                    'request_number' | 'submitter_full_name'
                > & {
                    request_number: number;
                })[];
            }) => ({
                count: response?.count,
                filteredCount: response?.filtered_count,
                filters: response?.filters,
                requests: response?.requests?.map((request) => ({
                    ...request,
                    request_number: request.request_number?.toString().padStart(6, '0'),
                })),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving requests.'),
            providesTags: [TagType.REQUEST_LIST],
        }),
        getRequestDetails: build.query<RequestDetails, { payerId: string; requestId: string }>({
            query: ({ payerId, requestId }) => `${payerId}/requests/${requestId}`,
            // eslint-disable-next-line complexity
            transformResponse: (response: {
                request_id: string;
                request_type: Exclude<
                    (typeof RequestType)[keyof typeof RequestType],
                    typeof RequestType.ARCHIVE
                >;
                request_sub_type?: string;
                type_level_2?: string;
                summary?: string;
                segment_id: string;
                member_id?: string;
                first_name?: string;
                last_name?: string;
                date_of_birth?: string;
                benefit_group?: string;
                create_date: string;
                submitter_upn: string;
                request_number: number;
                reopened_from_request_id?: string;
                reopened_from_request_number?: number;
                payer_assigned_upn?: string;
                issue_source?: string;
                primary_issue_description?: string;
                secondary_issue_description?: string;
                request_status: (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses];
                urgent: boolean;
                pbm_only?: boolean;
                additional_notes?: string;
                treatment_ids: string[];
                attachments: {
                    request_attachment_id: string;
                    filename: string;
                    date_added: string;
                }[];
                activity: {
                    request_activity_id: string;
                    action_date: string;
                    action_type: (typeof RequestActionType)[keyof typeof RequestActionType];
                    actor_upn?: string;
                    fmh_action: boolean;
                    action_detail?: string;
                }[];
            }) => ({
                requestId: response?.request_id,
                segmentId: response?.segment_id,
                requestType: response?.request_type,
                requestSubType: response?.request_sub_type,
                typeLevel2: response?.type_level_2,
                summary: response?.summary,
                memberId: response?.member_id,
                firstName: response?.first_name,
                lastName: response?.last_name,
                dateOfBirth: response?.date_of_birth,
                benefitGroup: response?.benefit_group,
                createDate: response?.create_date,
                submitterUpn: response?.submitter_upn,
                payerAssignedUpn: response?.payer_assigned_upn,
                requestNumber: response?.request_number?.toString().padStart(6, '0'),
                reopenedFromRequestId: response?.reopened_from_request_id,
                reopenedFromRequestNumber: response?.reopened_from_request_number
                    ?.toString()
                    .padStart(6, '0'),
                issueSource: response?.issue_source,
                primaryIssueDescription: response?.primary_issue_description,
                secondaryIssueDescription: response?.secondary_issue_description,
                requestStatus: response?.request_status,
                urgent: response?.urgent,
                additionalNotes: response?.additional_notes,
                treatmentIds: response?.treatment_ids,
                attachments: response?.attachments?.map((attachment) => ({
                    requestAttachmentId: attachment.request_attachment_id,
                    filename: attachment.filename,
                    dateAdded: attachment.date_added,
                })),
                activity: response?.activity?.map((action) => ({
                    requestActivityId: action.request_activity_id,
                    actionDate: action.action_date,
                    actionType: action.action_type,
                    actorUpn: action.actor_upn,
                    fmhAction: action.fmh_action,
                    actionDetail: action.action_detail,
                })),
                pbmOnly: response?.pbm_only,
            }),
            transformErrorResponse: handleRTKQueryError('Unable to display request.'),
            providesTags: (_result, _error, { requestId }) => [
                { type: TagType.REQUESTS, id: requestId },
            ],
        }),
        getRequestsForTreatment: build.query<
            RequestDetailsSummary[],
            { payerId: string; treatmentId: string }
        >({
            query: ({ payerId, treatmentId }) => `${payerId}/treatment/${treatmentId}`,
            transformResponse: (
                response: {
                    request_id: string;
                    request_number: number;
                    request_sub_type?: string;
                    type_level_2?: string;
                    request_status: (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses];
                    create_date: string;
                    completed_date?: string;
                    primary_issue_description?: string;
                    secondary_issue_description?: string;
                    issue_source?: string;
                    pbm_only?: boolean;
                }[],
            ) =>
                response?.map((request) => ({
                    requestId: request.request_id,
                    requestNumber: request.request_number?.toString().padStart(6, '0'),
                    requestSubType: request.request_sub_type,
                    typeLevel2: request.type_level_2,
                    requestStatus: request.request_status,
                    createDate: request.create_date,
                    completedDate: request.completed_date,
                    primaryIssueDescription: request.primary_issue_description,
                    secondaryIssueDescription: request.secondary_issue_description,
                    issueSource: request.issue_source,
                    pbmOnly: request.pbm_only,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving requests.'),
            providesTags: (_result, _error, { treatmentId }) => [
                { type: TagType.TREATMENT_REQUESTS, id: treatmentId },
            ],
        }),
        postNewRequest: build.mutation<
            { requestId: string; unverifiedMember: boolean },
            RequestPayload
        >({
            query: ({
                payerId,
                segmentId,
                requestType,
                requestSubType,
                typeLevel2,
                memberId,
                firstName,
                lastName,
                dateOfBirth,
                urgent,
                pbmOnly,
                additionalNotes,
                treatmentIds,
                files,
                assignedUpn,
                payerAssignedUpn,
            }) => ({
                url: `${payerId}/requests`,
                method: 'POST',
                body: jsonToFormData({
                    segment_id: segmentId,
                    request_type: requestType,
                    request_sub_type: requestSubType,
                    type_level_2: typeLevel2,
                    member_id: memberId,
                    first_name: firstName,
                    last_name: lastName,
                    date_of_birth: dateOfBirth,
                    urgent,
                    pbm_only: pbmOnly,
                    additional_notes: additionalNotes,
                    treatment_ids: treatmentIds,
                    files,
                    assigned_upn: assignedUpn,
                    payer_assigned_upn: payerAssignedUpn,
                }),
            }),
            transformResponse: (response: { request_id: string; unverified_member: boolean }) => ({
                requestId: response?.request_id?.toLocaleLowerCase(),
                unverifiedMember: response?.unverified_member,
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred creating request.'),
            invalidatesTags: (_, error, { treatmentIds }) =>
                !error
                    ? [
                          TagType.REQUESTS_COUNTS,
                          TagType.REQUEST_LIST,
                          ...(treatmentIds ?? []).map((id) => ({
                              type: TagType.TREATMENT_REQUESTS,
                              id,
                          })),
                      ]
                    : [],
        }),
        postMarkRequestRead: build.mutation<void, { payerId: string; requestId: string }>({
            query: ({ payerId, requestId }) => ({
                url: `${payerId}/requests/${requestId}/read`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred marking request as read.',
            ),
            invalidatesTags: (_, error) =>
                !error ? [TagType.REQUESTS_COUNTS, TagType.REQUEST_LIST] : [],
        }),
        postRequestActivity: build.mutation<
            string,
            {
                payerId: string;
                requestId: string;
                actionType: (typeof RequestActionType)[keyof typeof RequestActionType];
                actionDetail?: string;
            }
        >({
            query: ({ payerId, requestId, actionType, actionDetail }) => ({
                url: `${payerId}/requests/${requestId}/activity`,
                method: 'POST',
                body: { action_type: actionType, action_detail: actionDetail },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred updating request.'),
            invalidatesTags: (_, error, { requestId }) =>
                !error
                    ? [
                          { type: TagType.REQUESTS, id: requestId },
                          TagType.REQUEST_LIST,
                          TagType.REQUESTS_COUNTS,
                      ]
                    : [],
        }),
        postAttachFile: build.mutation<
            void,
            { payerId: string; requestId: string; attachment: File }
        >({
            query: ({ payerId, requestId, attachment }) => ({
                url: `${payerId}/requests/${requestId}/attachment`,
                method: 'POST',
                body: jsonToFormData({ attachment }),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred uploading file.'),
            invalidatesTags: (_, error, { requestId }) =>
                !error ? [{ type: TagType.REQUESTS, id: requestId }] : [],
        }),
    }),
});

export const {
    useGetOpenRequestCountsQuery,
    useGetOpenRequestsByTypeQuery,
    useGetArchivedRequestsQuery,
    useGetRequestDetailsQuery,
    useGetRequestsForTreatmentQuery,
    usePostMarkRequestReadMutation,
    usePostNewRequestMutation,
    usePostRequestActivityMutation,
    usePostAttachFileMutation,
} = payerRequestApi;
